import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'wouter'
import { ouvrirDevenirProducteur } from '../components/DevenirProducteurSheet'
import { ROUTES, TAB_PAR_CHEMIN, cheminOnglet } from '../routes'
import { useCasquette } from './casquette'
import { MVP } from '../mvp'

/**
 * Navigation du portail (S41 — ADR-067 ; machine fusionnee ici en S52).
 *
 * `useMachineNavigation` (la machine d'etat, URL comprise — ci-dessous) n'est
 * instanciee qu'UNE fois, par le Provider. Les composants consomment ensuite
 * ce dont ils ont besoin par hook, a n'importe quelle profondeur.
 *
 * DEUX contextes, pas un (maitrise des re-renders) :
 *  - ETAT (`useNavigationPortail`)   : change a chaque navigation — ne le
 *    consommer que si on AFFICHE quelque chose qui en depend ;
 *  - ACTIONS (`useActionsPortail`)   : valeur STABLE (identite constante via
 *    ref) — un composant qui ne fait que declencher (bouton, carte) ne
 *    re-rend jamais a cause de la navigation.
 *
 * ⚠️ Ne JAMAIS remonter ici d'etat haute frequence (saisie, scroll,
 * brouillons) : il re-rendrait tous les consommateurs de l'ETAT.
 *
 */

// Cles de sous-vue du Compte (CTA RINA, FAQ) -> chemin (S55).
const CHEMIN_VUE_COMPTE = {
  main: ROUTES.compte.base,
  profile: ROUTES.compte.profil,
  securite: ROUTES.compte.securite,
  dossier: ROUTES.compte.dossier,
  pin: ROUTES.compte.pin,
  aide: ROUTES.compte.aide,
  aideFaq: ROUTES.compte.aideFaq,
  aideContact: ROUTES.compte.aideContact,
}

const CLE_INTRO_VUE = 'manrina_intro_vue'

// --- La machine (URL = source de verite, ADR-065/068) ---
function useMachineNavigation() {
  const [path, setLocation] = useLocation()

  // --- Derivations depuis l'URL --------------------------------------------
  const page = path === ROUTES.design ? 'design' : path === ROUTES.connexion ? 'connexion' : 'marche'

  const openMarcheProducer = path === ROUTES.producteur.marche
  const openPaniers = path === ROUTES.paniers
  const openPaniersProducer = path === ROUTES.producteur.paniers
  const openMarcheId = path.startsWith(ROUTES.marche.prefixe) ? path.slice(ROUTES.marche.prefixe.length) : null

  // Onglet du hub porte par l'URL (table TAB_PAR_CHEMIN — couvre les deux
  // casquettes), ou null si l'URL designe autre chose (module, page, inconnu).
  const ongletDepuisUrl = TAB_PAR_CHEMIN[path] ?? null

  // Dernier onglet connu — sert au « lieu » de RINA quand on est dans un
  // module (l'URL est alors celle du module, plus celle d'un onglet).
  const [hubTab, setHubTabLocal] = useState(() => ongletDepuisUrl ?? 'home')
  useEffect(() => {
    if (ongletDepuisUrl) setHubTabLocal(ongletDepuisUrl)
  }, [ongletDepuisUrl])
  // Casquette : contexte dedie (S45) — lue pour resoudre le chemin d'un
  // onglet ('home' → / ou /producteur) et basculee via changerCasquette.
  const { hubMode, setHubMode } = useCasquette()

  // Sous /producteur, l'URL FAIT FOI (ADR-068) : entrer dans l'espace
  // producteur bascule la casquette — remplace ZoneProducteur/les gardes.
  useEffect(() => {
    if (path.startsWith(ROUTES.producteur.base) && hubMode !== 'producer') {
      setHubMode('producer')
    }
  }, [path, hubMode])

  // Derniere page visitee HORS du Compte (S62) : c'est la que ramene le
  // « Retour » du Compte. L'historique navigateur ne convient pas — apres
  // Compte → Profil → « ← Compte », la page precedente est... le Profil.
  const horsCompteRef = useRef(cheminOnglet('home', hubMode))
  useEffect(() => {
    if (!path.startsWith(ROUTES.compte.base)) horsCompteRef.current = path
  }, [path])
  const quitterCompte = () => { setLocation(horsCompteRef.current || cheminOnglet('home', hubMode)); window.scrollTo(0, 0) }

  const setHubTab = (tab) => {
    const chemin = cheminOnglet(tab, hubMode)
    if (path !== chemin) setLocation(chemin)
  }

  // Un module plein ecran est-il actif ? L'URL suffit (prefixe /producteur —
  // plus de condition de casquette depuis l'ADR-068).
  const moduleOuvert = openMarcheProducer || !!openMarcheId || openPaniersProducer || openPaniers

  // --- Etat qui reste en memoire -------------------------------------------
  // Ecran immersif — jamais sur un deep-link (arriver directement sur
  // /marche/xxx ne doit pas l'ouvrir). MVP (S62) : PREMIERE visite seulement,
  // sauf MVP.INTRO_CHAQUE_VISITE ; le marqueur est pose des qu'on l'affiche.
  const [showImmersive, setShowImmersive] = useState(() => {
    if (window.location.pathname !== ROUTES.accueil) return false
    if (MVP.INTRO_CHAQUE_VISITE) return true
    try {
      if (localStorage.getItem(CLE_INTRO_VUE)) return false
      localStorage.setItem(CLE_INTRO_VUE, '1')
    } catch { /* stockage indisponible : on affiche */ }
    return true
  })

  // Les SOUS-VUES (Compte, Explorer, paiement du Wallet) sont des etats
  // LOCAUX de leurs pages depuis S52 : les pages se demontent au changement
  // d'onglet (semantique Routes), le reset est naturel. Les entrees externes
  // passent par l'URL (/account/securite..., /producteur/marche?tab=...).
  // Les feuilles/fiches (profil, devenir producteur, fiche producteur) passent
  // par le gestionnaire imperatif `contexts/modales.jsx` (ADR-066) — plus
  // aucun etat d'overlay ici, chaque modale a son OUVREUR colocalise.
  // RINA chat modal (separe de l'ecran immersif — ecran d'application, pas une modale)
  const [showRinaChat, setShowRinaChat] = useState(false)
  const handleRinaClick = () => {
    if (!MVP.RINA_CHAT) return // MVP (S62) : pas de page de conversation
    if (showRinaChat) {
      setShowRinaChat(false)
    } else {
      setShowRinaChat(true)
      // Fermer l'immersif si ouvert
      if (showImmersive) setShowImmersive(false)
    }
  }

  // --- Actions de navigation -----------------------------------------------
  const openMarche = (id) => { if (id) { setShowImmersive(false); setLocation(ROUTES.marche.vers(id)); window.scrollTo(0, 0) } }
  // `init` { tab, histoSel } → query params : deep-linkable et sans etat fantome
  // (navigation croisee v7.2.3 : une facture ouvre Historique > detail).
  const ouvrirMarcheProducteur = (init = null) => {
    setShowImmersive(false)
    // S62 : sans module plein ecran, « ouvrir le Marche » = l'onglet Évènements
    // du hub (un onglet precis — historique, stand — garde le module).
    if (!MVP.MARCHE_PRODUCTEUR_PLEIN_ECRAN && !init?.tab) {
      setLocation(ROUTES.producteur.marches)
      window.scrollTo(0, 0)
      return
    }
    const q = new URLSearchParams()
    if (init?.tab) q.set('tab', init.tab)
    if (init?.histoSel) q.set('sel', init.histoSel)
    setLocation(ROUTES.producteur.marche + (q.size ? `?${q}` : ''))
    window.scrollTo(0, 0)
  }
  const ouvrirPaniers = () => { setShowImmersive(false); setLocation(ROUTES.paniers); window.scrollTo(0, 0) }
  const ouvrirPaniersProducteur = () => { setShowImmersive(false); setLocation(ROUTES.producteur.paniers); window.scrollTo(0, 0) }
  // Ouvrir le Compte sur une sous-vue precise (CTA RINA) : chaque sous-vue a
  // SON chemin (S55) — table VUE_COMPTE ci-dessous (cles historiques des CTA).
  const ouvrirCompte = (vue = null) => {
    setShowImmersive(false)
    setLocation(CHEMIN_VUE_COMPTE[vue] || ROUTES.compte.base)
    window.scrollTo(0, 0)
  }
  // Sortir d'un module = revenir a un onglet du hub (le module etant porte par
  // l'URL, la navigation le ferme d'elle-meme).
  const fermerModule = (tab = (hubMode === 'producer' ? MVP.EXPLORER_PRODUCTEUR : MVP.EXPLORER_CLIENT) ? 'explore' : 'home') => { setHubTab(tab) }

  /**
   * Changer de casquette. Revenir a l'accueil ferme TOUS les modules plein
   * ecran (portes par l'URL) : sans ca, on restait bloque dans le module
   * ouvert et le changement ne se voyait qu'en ressortant (bug remonte par
   * Enzo le 23 juil depuis le module Paniers client).
   * Depuis la page Compte, on BASCULE SUR PLACE (proto v7.4 : `pickLens` ne
   * change pas d'ecran) : `resterSurPlace` saute le retour a l'accueil.
   */
  const changerCasquette = (m, { resterSurPlace = false } = {}) => {
    setHubMode(m)
    // L'accueil de chaque casquette a SON adresse (ADR-068)
    if (!resterSurPlace) setLocation(m === 'producer' ? ROUTES.producteur.accueil : ROUTES.accueil)
  }

  // Redirection d'entree historique (lien diffuse avant l'ADR-065).
  // NB : /producteur n'est plus une redirection — c'est l'ACCUEIL producteur
  // (ADR-068), la bascule de casquette est faite par ZoneProducteur (App.jsx).
  useEffect(() => {
    if (window.location.pathname === ROUTES.devenirProducteur) {
      // persisteALaNavigation : la redirection vers / qui suit ne doit pas la fermer
      ouvrirDevenirProducteur({ persisteALaNavigation: true })
      setLocation(ROUTES.accueil, { replace: true })
    }
  }, [])

  // Facade de navigation par NOM DE PAGE, conservee pour les pages (elles ne
  // connaissent pas les chemins) : 'marche' | 'design' | 'connexion' |
  // 'devenir-producteur' | 'profil'.
  const navigate = (newPage, opts = {}) => {
    // « Devenir producteur » = modale overlay v4 (pas une page) — ouverte depuis le Compte
    if (newPage === 'devenir-producteur') {
      setShowImmersive(false)
      ouvrirDevenirProducteur()
      return
    }
    // « profil » a desormais sa vraie adresse (S55)
    if (newPage === 'profil') {
      setShowImmersive(false)
      setLocation(ROUTES.compte.profil)
      window.scrollTo(0, 0)
      return
    }
    // Retour au portail = ecran immersif par defaut, sauf si l'appelant veut explicitement le hub
    if (newPage === 'marche') {
      setShowImmersive(opts.immersive !== false)
      setLocation(cheminOnglet('home', hubMode))
      window.scrollTo(0, 0)
      return
    }
    setShowImmersive(false)
    setLocation(newPage === 'design' ? ROUTES.design : newPage === 'connexion' ? ROUTES.connexion : ROUTES.accueil)
    window.scrollTo(0, 0)
  }

  return {
    page, navigate,
    showImmersive, setShowImmersive,
    hubTab, setHubTab,
    changerCasquette, ouvrirCompte, quitterCompte,
    openMarcheId, openMarche,
    openMarcheProducer, ouvrirMarcheProducteur,
    openPaniers, ouvrirPaniers,
    openPaniersProducer, ouvrirPaniersProducteur,
    fermerModule,
    moduleOuvert,
    showRinaChat, setShowRinaChat, handleRinaClick,
  }
}

// --- Exposition : deux contextes (etat / actions stables) ---
const EtatCtx = createContext(null)
const ActionsCtx = createContext(null)

export function NavigationPortailProvider({ children }) {
  const nav = useMachineNavigation()

  // Les actions lisent la DERNIERE version de la machine via une ref : leur
  // identite ne change jamais, le contexte ACTIONS ne re-rend personne.
  const navRef = useRef(nav)
  navRef.current = nav
  const actions = useMemo(() => ({
    navigate: (...a) => navRef.current.navigate(...a),
    setHubTab: (...a) => navRef.current.setHubTab(...a),
    changerCasquette: (...a) => navRef.current.changerCasquette(...a),
    openMarche: (...a) => navRef.current.openMarche(...a),
    ouvrirMarcheProducteur: (...a) => navRef.current.ouvrirMarcheProducteur(...a),
    ouvrirPaniers: () => navRef.current.ouvrirPaniers(),
    ouvrirPaniersProducteur: () => navRef.current.ouvrirPaniersProducteur(),
    fermerModule: (...a) => navRef.current.fermerModule(...a),
    handleRinaClick: () => navRef.current.handleRinaClick(),
    ouvrirCompte: (...a) => navRef.current.ouvrirCompte(...a),
    quitterCompte: () => navRef.current.quitterCompte(),
    setShowImmersive: (...a) => navRef.current.setShowImmersive(...a),
    setShowRinaChat: (...a) => navRef.current.setShowRinaChat(...a),
  }), [])

  const etat = {
    page: nav.page,
    hubTab: nav.hubTab,
    moduleOuvert: nav.moduleOuvert,
    openMarcheId: nav.openMarcheId,
    openMarcheProducer: nav.openMarcheProducer,
    openPaniers: nav.openPaniers,
    openPaniersProducer: nav.openPaniersProducer,
    showImmersive: nav.showImmersive,
    showRinaChat: nav.showRinaChat,
  }

  return (
    <ActionsCtx.Provider value={actions}>
      <EtatCtx.Provider value={etat}>{children}</EtatCtx.Provider>
    </ActionsCtx.Provider>
  )
}

function exiger(ctx, nom) {
  const v = useContext(ctx)
  if (!v) throw new Error(`${nom} hors de NavigationPortailProvider`)
  return v
}

/** Etat de navigation (re-rend a chaque navigation). */
export function useNavigationPortail() { return exiger(EtatCtx, 'useNavigationPortail') }
/** Actions de navigation — identite STABLE, aucun re-render induit. */
export function useActionsPortail() { return exiger(ActionsCtx, 'useActionsPortail') }
